import React, { useState } from 'react';
import type { PointsConfig } from '@sulis-liiga/common';

interface PointsConfigFormProps {
  initialData?: PointsConfig;
  onSubmit: (data: PointsConfig) => void;
  onCancel: () => void;
  loading: boolean;
}

const PointsConfigForm: React.FC<PointsConfigFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState<PointsConfig>({
    win: initialData?.win ?? 3,
    loss: initialData?.loss ?? 0,
    draw: initialData?.draw ?? 1,
    walkover: initialData?.walkover ?? 3,
    noShow: initialData?.noShow ?? 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.win < 0) {
      newErrors.win = 'Win points cannot be negative';
    }

    if (formData.loss < 0) {
      newErrors.loss = 'Loss points cannot be negative';
    }

    if (formData.draw !== undefined && formData.draw < 0) {
      newErrors.draw = 'Draw points cannot be negative';
    }

    if (formData.walkover !== undefined && formData.walkover < 0) {
      newErrors.walkover = 'Walkover points cannot be negative';
    }

    if (formData.noShow !== undefined && formData.noShow < 0) {
      newErrors.noShow = 'No show points cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? undefined : parseInt(value),
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Configure the points awarded for different match outcomes. Common settings: Win (3),
              Draw (1), Loss (0).
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="win" className="block text-sm font-medium text-gray-700">
            Win Points
          </label>
          <input
            type="number"
            name="win"
            id="win"
            required
            min="0"
            value={formData.win}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.win ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {errors.win && <p className="mt-1 text-sm text-danger-600">{errors.win}</p>}
        </div>

        <div>
          <label htmlFor="loss" className="block text-sm font-medium text-gray-700">
            Loss Points
          </label>
          <input
            type="number"
            name="loss"
            id="loss"
            required
            min="0"
            value={formData.loss}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.loss ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {errors.loss && <p className="mt-1 text-sm text-danger-600">{errors.loss}</p>}
        </div>

        <div>
          <label htmlFor="draw" className="block text-sm font-medium text-gray-700">
            Draw Points (Optional)
          </label>
          <input
            type="number"
            name="draw"
            id="draw"
            min="0"
            value={formData.draw ?? ''}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.draw ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {errors.draw && <p className="mt-1 text-sm text-danger-600">{errors.draw}</p>}
        </div>

        <div>
          <label htmlFor="walkover" className="block text-sm font-medium text-gray-700">
            Walkover Win Points (Optional)
          </label>
          <input
            type="number"
            name="walkover"
            id="walkover"
            min="0"
            value={formData.walkover ?? ''}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.walkover ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {errors.walkover && <p className="mt-1 text-sm text-danger-600">{errors.walkover}</p>}
        </div>

        <div>
          <label htmlFor="noShow" className="block text-sm font-medium text-gray-700">
            No Show Penalty Points (Optional)
          </label>
          <input
            type="number"
            name="noShow"
            id="noShow"
            min="0"
            value={formData.noShow ?? ''}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.noShow ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {errors.noShow && <p className="mt-1 text-sm text-danger-600">{errors.noShow}</p>}
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : 'Save Points Configuration'}
        </button>
      </div>
    </form>
  );
};

export default PointsConfigForm;
